import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import api from "../../Utils/api";
import ImportResultsDialog from "./AddResultsDialog";

const REQUIRED_FIELDS = [
  "First Name",
  "Last Name",
  "Email",
  "Start Date",
  "Start Time",
  "End Date",
  "End Time",
  "Break Start Time",
  "Break End Time",
  "Location",
  "Area",
];

function TablePreviewDialog({
  open,
  onClose,
  headers,
  parsedRows,
  getMissingFields,
}) {
  const json_headers = [
    "first_name",
    "last_name",
    "email",
    "date",
    "startTime",
    "end_date",
    "endTime",
    "breakStartTime",
    "breakEndTime",
    "location_name",
    "location_area",
  ];

  const [showResults, setShowResults] = useState(false);
  const [importResults, setImportResults] = useState(null);

  if (!open) return null;
  const json = parsedRows.map((row) => {
    const obj = {};
    json_headers.forEach((header, idx) => {
      obj[header] = row[idx] !== undefined ? row[idx] : "";
    });
    return obj;
  });

  const sendData = async () => {
    const res = await api.post("/schedule/add_from_file", {
      schedules: json,
    });

    // setShowTableDialog(false);

    if (res.data.success) {
      toast.success(res.data.message);
      setImportResults(res.data.results);
      setShowResults(true);
    } else {
      toast.error(res.data.message);
    }
  };

  const closeImportResult = () => {
    setShowResults(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl p-8 relative">
        <button
          className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h3 className="font-semibold mb-4">Preview Uploaded Data</h3>
        <div className="max-h-[60vh] overflow-auto border rounded">
          <table className="min-w-full border text-xs">
            <thead>
              <tr>
                <th className="border px-2 py-1 bg-gray-100">No</th>
                {headers.map((header, idx) => (
                  <th key={idx} className="border px-2 py-1 bg-gray-100">
                    {header}
                  </th>
                ))}
                <th className="border px-2 py-1 bg-gray-100 text-red-700">
                  Missing Fields
                </th>
              </tr>
            </thead>
            <tbody>
              {parsedRows.map((row, rowIdx) => {
                const missing = getMissingFields(row);
                return (
                  <tr
                    key={rowIdx}
                    className={missing.length ? "bg-red-50" : ""}
                  >
                    <td className="border px-2 py-1">{rowIdx + 1}</td>
                    {headers.map((_, colIdx) => (
                      <td key={colIdx} className="border px-2 py-1">
                        {row[colIdx] || ""}
                      </td>
                    ))}
                    <td className="border px-2 py-1 text-red-700">
                      {missing.length > 0 ? missing.join(", ") : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {parsedRows.some((row) => getMissingFields(row).length) > 0 ? (
          <div className="mt-2 text-sm text-red-600">
            Some rows have missing required fields. Please fix them in your file
            and re-upload.
          </div>
        ) : (
          <button
            className="bg-gray-100 m-2 p-2 text-sm text-gray-700 rounded-md hover:bg-gray-700 hover:text-white"
            onClick={sendData}
            aria-label="Close"
          >
            Continue
          </button>
        )}
      </div>

      {showResults && (
        <ImportResultsDialog
          open={showResults}
          onClose={closeImportResult}
          results={importResults}
        />
      )}
    </div>
  );
}

const AddShiftsFromFileDialog = ({
  open,
  onClose,
  getSchedules,
  onContinue,
  onViewHistory,
}) => {
  const fileInputRef = useRef(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [showTableDialog, setShowTableDialog] = useState(false);

  if (!open) return null;

  const templateData = [
    {
      "First Name": "Tester",
      "Last Name": "Testson",
      Email: "tester@d",
      "Start Date": "2022-11-11",
      "Start Time": "09:00",
      "End Date": "2022-11-11",
      "End Time": "17:00",
      "Break Start Time": "12:00 PM",
      "Break End Time": "12:30 PM",
      Location: "Location 1",
      Area: "Office",
    },
  ];

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "template.xlsx");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await readFile(file);
      const jsonData = convertToJson(data, file).filter(
        (item) => item.length != 0
      );
      if (!jsonData.length) {
        toast.error("No data found in file.");
        return;
      }
      setHeaders(jsonData[0]);

      setParsedRows(jsonData.slice(1));
      setShowTableDialog(true); // Open the table dialog
    } catch (error) {
      console.error("File processing error:", error);
      toast.error("Failed to process file");
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);

      if (file.name.endsWith(".xlsx")) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const convertToJson = (data, file) => {
    const workbook = XLSX.read(data, {
      type: file.name.endsWith(".xlsx") ? "array" : "binary",
    });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(firstSheet, {
      header: 1, // Array of arrays, first row is headers
      raw: false,
      dateNF: "m/d/yyyy",
    });
  };

  // Helper to check missing fields in a row
  const getMissingFields = (row) => {
    if (!headers.length) return [];
    return REQUIRED_FIELDS.filter((field) => {
      const idx = headers.indexOf(field);
      return idx === -1 || !row[idx] || String(row[idx]).trim() === "";
    });
  };

  const closeTableDialog = () => {
    setShowTableDialog(false);
    onClose();
    getSchedules();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 relative">
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-gray-700"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 rounded-full p-4">
              {/* Replace with your SVG or image */}
              <svg width="120" height="120" fill="none" viewBox="0 0 24 24">
                <rect
                  width="24"
                  height="24"
                  rx="12"
                  fill="#7C3AED"
                  fillOpacity="0.1"
                />
                <path
                  d="M7 10h10M7 14h6M9 6h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
                  stroke="#7C3AED"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {/* Title */}
          <h2 className="text-center text-xl font-semibold mb-6">
            Add shifts from file
          </h2>
          {/* How to add shifts */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-gray-700">
                How to add shifts
              </span>
              <a href="#" className="text-xs text-gray-700 hover:underline">
                More info
              </a>
            </div>
            <ol className="space-y-3">
              <li className="bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between">
                <span>1. Download the schedule template</span>
                <div className="relative">
                  <button
                    className="bg-gray-50 text-gray-700 font-medium px-3 py-1 rounded-lg hover:bg-gray-100"
                    onClick={downloadTemplate}
                  >
                    Download
                  </button>
                </div>
              </li>
              <li className="bg-gray-50 rounded-lg px-4 py-3">
                <span>2. Paste your shift data into the template</span>
                <ul className="mt-1 ml-4 text-xs text-gray-500 list-disc">
                  <li>
                    bulk-import-update.import-dialog.roster.instructions.pay-rate-disclaimer
                  </li>
                </ul>
              </li>
              <li className="bg-gray-50 rounded-lg px-4 py-3">
                <span>3. Upload your updated file</span>
              </li>
            </ol>
          </div>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          {/* Continue button */}
          <button
            className="w-full bg-gray-700 text-white font-semibold py-3 rounded-lg hover:bg-gray-800 transition mb-2"
            onClick={() => fileInputRef.current.click()}
          >
            Continue to upload
          </button>
        </div>
      </div>
      {/* Table Preview Dialog */}
      <TablePreviewDialog
        open={showTableDialog}
        onClose={closeTableDialog}
        headers={headers}
        parsedRows={parsedRows}
        getMissingFields={getMissingFields}
      />
    </>
  );
};

export default AddShiftsFromFileDialog;
